"use client"

import { useRef } from "react"
import Link from "next/link"
import { cases, type CaseId } from "@/app/content/landing"
import { ArrowUpRight, ChevronLeft, ChevronRight } from "@/app/components/icons"
import { iconButtonClass } from "@/app/components/styles"

export function CaseRail() {
  const scrollerRef = useRef<HTMLDivElement>(null)

  function scrollByPage(direction: -1 | 1) {
    const scroller = scrollerRef.current
    if (!scroller) return
    scroller.scrollBy({
      left: direction * Math.min(scroller.clientWidth * 0.85, 480),
      behavior: "smooth",
    })
  }

  return (
    <div>
      <div className="mb-5 flex items-center justify-end gap-2">
        <button
          type="button"
          className={iconButtonClass}
          aria-label="Предыдущие кейсы"
          onClick={() => scrollByPage(-1)}
        >
          <ChevronLeft />
        </button>
        <button
          type="button"
          className={iconButtonClass}
          aria-label="Следующие кейсы"
          onClick={() => scrollByPage(1)}
        >
          <ChevronRight />
        </button>
      </div>
      <div
        ref={scrollerRef}
        className="flex snap-x snap-mandatory gap-5 overflow-x-auto pb-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {cases.map((item) => (
          <article key={item.id} className="w-[84vw] shrink-0 snap-start sm:w-[440px]">
            <div className="relative">
              <CaseArt id={item.id} />
              <Link
                href="/#contact"
                className="absolute right-4 bottom-4 inline-flex size-12 items-center justify-center rounded-2xl bg-accent text-white shadow-lg transition-colors hover:bg-accent-strong"
                aria-label={`Обсудить проект ${item.title}`}
              >
                <ArrowUpRight />
              </Link>
            </div>
            <p className="mt-4 text-sm font-medium text-accent">{item.tag}</p>
            <h3 className="mt-1 text-2xl font-semibold tracking-tight">{item.title}</h3>
            <p className="mt-2 max-w-md text-sm leading-relaxed text-muted">{item.summary}</p>
          </article>
        ))}
      </div>
    </div>
  )
}

function CaseArt({ id }: { id: CaseId }) {
  if (id === "pulse") return <PulseArt />
  if (id === "lumen") return <LumenArt />
  return <ForgeArt />
}


function PulseArt() {
  return (
    <div className="relative h-64 overflow-hidden rounded-[28px] bg-[#eef8f1] sm:h-80">
      <div className="absolute top-8 right-8 size-24 rounded-full bg-[#3dce7a]" />
      <div className="absolute top-12 right-14 size-6 rounded-full bg-white" />
      <div className="absolute top-16 right-12 size-3 rounded-full bg-[#143026]/80" />
      <div className="relative max-w-[68%] space-y-3 p-5 sm:p-6">
        <div className="rounded-2xl rounded-bl-md bg-white px-4 py-3 text-sm shadow-sm">
          Завтра в 11:00 свободно
        </div>
        <div className="rounded-2xl rounded-bl-md bg-white px-4 py-3 text-sm shadow-sm">
          Записать на это время?
        </div>
        <div className="ml-6 rounded-2xl rounded-br-md bg-[#143026] px-4 py-3 text-sm text-white">
          Да, на имя Анны
        </div>
      </div>
    </div>
  )
}

function LumenArt() {
  return (
    <div className="grid h-64 grid-cols-2 gap-3 overflow-hidden rounded-[28px] bg-gradient-to-br from-[#312e81] to-[#7c3aed] p-5 pb-20 sm:h-80">
      <div className="rounded-2xl bg-white/15 p-4 text-white">
        <p className="text-xs text-white/70">Старт</p>
        <p className="mt-8 text-2xl font-semibold">1 900 ₽</p>
      </div>
      <div className="rounded-2xl bg-white p-4 text-foreground">
        <p className="text-xs text-muted">Команда</p>
        <p className="mt-8 text-2xl font-semibold">4 900 ₽</p>
      </div>
      <div className="col-span-2 rounded-2xl bg-white/10 px-4 py-3 text-sm text-white">
        Следующее списание 12 мая
      </div>
    </div>
  )
}

function ForgeArt() {
  return (
    <div className="flex h-64 gap-4 overflow-hidden rounded-[28px] bg-[#eef3ff] p-5 sm:h-80">
      <div className="flex w-14 flex-col gap-2">
        <div className="h-10 rounded-xl bg-accent" />
        <div className="h-10 rounded-xl bg-white" />
        <div className="h-10 rounded-xl bg-white" />
        <div className="h-10 rounded-xl bg-white" />
      </div>
      <div className="flex flex-1 items-end gap-2">
        <div className="h-[42%] flex-1 rounded-xl bg-[#93c5fd]" />
        <div className="h-[70%] flex-1 rounded-xl bg-accent" />
        <div className="h-[55%] flex-1 rounded-xl bg-[#1e3a8a]" />
        <div className="h-[86%] flex-1 rounded-xl bg-[#1d4ed8]" />
      </div>
    </div>
  )
}
